import React from 'react';
import { styled, keyframes } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import Placeholder from '@atlassian/jira-placeholder';
import type { async_softwareReleasesVersionDetailIssueList_FiltersAsync$key } from '@atlassian/jira-relay/src/__generated__/async_softwareReleasesVersionDetailIssueList_FiltersAsync.graphql';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import type { Filters } from './index';

const FiltersLazy = lazyForPaint<typeof Filters>(() =>
	import(
		/* webpackChunkName: "async-jira-software-releases-version-detail-issue-list-filters" */ './index'
	).then((mod) => mod.Filters),
);

export const Loading = () => (
	<FilterBarLoadingWraper>
		<TextFilterSkeleton />
		<AssigneeFilterSkeleton />
		<DropdownFilterSkeleton />
		<DropdownFilterSkeleton />
	</FilterBarLoadingWraper>
);

interface Props {
	jiraVersion: async_softwareReleasesVersionDetailIssueList_FiltersAsync$key;
}

export const FiltersAsync = ({ jiraVersion }: Props) => {
	const jiraVersionData = useFragment(
		graphql`
			fragment async_softwareReleasesVersionDetailIssueList_FiltersAsync on JiraVersion {
				...filters_softwareReleasesVersionDetailIssueList
			}
		`,
		jiraVersion,
	);

	return (
		<JSErrorBoundary
			fallback="flag"
			id="jira.software.releases.version.detail.issue.list.filters"
			packageName="jira-software-releases-version-detail-issue-list"
			teamName="fusion-solaris"
		>
			<Placeholder
				name="jira-software-releases-version-detail-issue-list-filters"
				fallback={<Loading />}
			>
				<FiltersLazy jiraVersion={jiraVersionData} />
			</Placeholder>
		</JSErrorBoundary>
	);
};

const skeletonKeyFrames = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '2000px 0',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AnimationBaseDiv = styled.div({
	animationDelay: '0s',
	animationDuration: '2s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationTimingFunction: 'linear',
	backgroundImage: `linear-gradient( to right, ${token(
		'color.skeleton',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		colors.N30,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	)} 10%, ${token('color.skeleton.subtle', colors.N20)} 30%, ${token(
		'color.skeleton',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		colors.N30,
	)} 50% )`,
	backgroundRepeat: 'no-repeat',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.skeleton', colors.N30),
	animationName: skeletonKeyFrames,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterBarLoadingWraper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
	width: '100%',
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextFilterSkeleton = styled(AnimationBaseDiv)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize * 0.5}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 15}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	marginRight: `${gridSize * 2}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AssigneeFilterSkeleton = styled(AnimationBaseDiv)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize * 2}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 4}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 4}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	marginRight: `${gridSize * 2}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownFilterSkeleton = styled(AnimationBaseDiv)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize * 2}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 2.5}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 8}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	marginRight: `${gridSize * 2}px`,
});
